import React, { useEffect, useState, useRef, lazy } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, QuoteIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
const testimonialAuthors = [{
  author: 'Aleksandr Katerinich',
  company: 'Code-Smith',
  companyInitial: 'C',
  companyColor: 'bg-blue-500'
}, {
  author: 'Under NDA',
  company: 'SaaS Company',
  companyInitial: 'S',
  companyColor: 'bg-emerald-500'
}, {
  author: 'Vladimir Voloshin',
  company: 'Dizair',
  companyInitial: 'D',
  companyColor: 'bg-violet-500'
}, {
  author: 'Roman Dzhulay',
  company: 'Andromo LTD',
  companyInitial: 'A',
  companyColor: 'bg-amber-500'
}, {
  author: 'Yuliya Barabash',
  company: 'SBSB Fintech Lawyers',
  companyInitial: 'S',
  companyColor: 'bg-rose-500'
}, {
  author: 'Vladimir Kuriy',
  company: 'para.agency',
  companyInitial: 'P',
  companyColor: 'bg-teal-500'
}];
const clientLogos = [{
  name: 'Zepter',
  logo: 'https://cdn.magicpatterns.com/uploads/oimH959y7MFm95LWY6Mf4J/zepterlogo.png.webp',
  bgClass: 'bg-navy-900'
}, {
  name: 'Payler',
  logo: 'https://cdn.magicpatterns.com/uploads/2Tf5zq3Rg6ViugFDm1umxq/Payler_LOGO.svg',
  bgClass: 'bg-white'
}, {
  name: 'SBSB',
  logo: 'https://cdn.magicpatterns.com/uploads/3r829MxxY4Zb8rmDEqw7ya/logo_white.svg',
  bgClass: 'bg-navy-900'
}, {
  name: 'PPL Digital Solutions',
  logo: 'https://cdn.magicpatterns.com/uploads/eG8FkXSDcMzJC2cLkUkPoe/ppl-09.png.webp',
  bgClass: 'bg-white'
}, {
  name: 'RocketFirm',
  logo: 'https://cdn.magicpatterns.com/uploads/5w3zS993Pxoqj95Hy2vzMQ/logo_(2).svg',
  bgClass: 'bg-navy-900'
}, {
  name: 'DoktorPro',
  logo: 'https://cdn.magicpatterns.com/uploads/5nwZaaK5RLvjRA1VYMUZ1N/photo.png.webp',
  bgClass: 'bg-white'
}, {
  name: 'IT4Medicine',
  logo: 'https://cdn.magicpatterns.com/uploads/73hYRhWL7ZkuTp9xsY1g77/tild3035-3836-4732-b.png.webp',
  bgClass: 'bg-white'
}, {
  name: 'DevPower',
  logo: 'https://cdn.magicpatterns.com/uploads/gRKRFNNBxY2ak5judA1bpM/logo.png.webp',
  bgClass: 'bg-white'
}, {
  name: 'Finbert',
  logo: 'https://cdn.magicpatterns.com/uploads/iegHNv4UYoz22TzxaDmyEu/logo_1.svg',
  bgClass: 'bg-white'
}, {
  name: 'SYNLAB',
  logo: 'https://cdn.magicpatterns.com/uploads/uW6JAwYH6bGamxicED36F3/photo.png_(3).webp',
  bgClass: 'bg-white'
}, {
  name: 'Vortex Plus',
  logo: 'https://cdn.magicpatterns.com/uploads/aWKfYkcsM7UGZfbvHM1eVs/__3.jpeg.webp',
  bgClass: 'bg-white'
}, {
  name: 'Sherpa',
  logo: 'https://cdn.magicpatterns.com/uploads/jrAWS1PJLLj4yAAbfysZg3/photo.png_(1).webp',
  bgClass: 'bg-white'
}, {
  name: 'Diamond Pharm',
  logo: 'https://cdn.magicpatterns.com/uploads/bATtCF4q8ST2tFfDTHwCv9/photo.png_(2).webp',
  bgClass: 'bg-white'
}, {
  name: 'British International School',
  logo: 'https://cdn.magicpatterns.com/uploads/dHFgbKCnhkZwU17PwUzhyY/photo.jpeg.webp',
  bgClass: 'bg-navy-900'
}, {
  name: 'Chipita',
  logo: 'https://cdn.magicpatterns.com/uploads/jVweTN6kAzoD2yBbjKELXV/__1.jpeg.webp',
  bgClass: 'bg-white'
}];
export function TestimonialsSection() {
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(0);
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const next = () => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % t.testimonials.items.length);
  };
  const prev = () => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + t.testimonials.items.length) % t.testimonials.items.length);
  };
  useEffect(() => {
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, []);
  const testimonial = t.testimonials.items[current];
  const author = testimonialAuthors[current];
  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 80 : -80,
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -80 : 80,
      opacity: 0
    })
  };
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" data-id="element-533">
      <div className="absolute inset-0 pointer-events-none" data-id="element-534">
        <div className="absolute top-0 left-1/3 w-[400px] h-[400px] rounded-full bg-primary-50/25 blur-3xl hidden sm:block" data-id="element-535" />
        <div className="absolute bottom-0 right-1/4 w-[300px] h-[300px] rounded-full bg-cyan-50/20 blur-3xl hidden sm:block" data-id="element-536" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-537">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="text-center mb-14" data-id="element-538">
          <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-539">
            {t.testimonials.eyebrow}
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-540">
            {t.testimonials.title}
          </h2>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.1
      }} className="grid grid-cols-3 sm:grid-cols-5 gap-4 sm:gap-5 max-w-4xl mx-auto mb-14" data-id="element-541">
          {clientLogos.map(logo => <div key={logo.name} className={`aspect-[3/2] rounded-xl border border-navy-100 flex items-center justify-center p-4 shadow-sm ${logo.bgClass}`} title={logo.name} data-id="element-542">
              <img src={logo.logo} alt={logo.name} className="w-full h-full object-contain" loading="lazy" data-id="element-543" />
            </div>)}
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="max-w-3xl mx-auto" data-id="element-544">
          <div className="relative bg-gradient-to-br from-navy-50/80 via-white to-primary-50/30 rounded-3xl p-8 sm:p-12 border border-navy-100 shadow-lg shadow-navy-100/30 overflow-hidden" data-id="element-545">
            <QuoteIcon className="absolute top-6 right-8 w-32 h-32 text-primary-100/40" data-id="element-546" />
            <QuoteIcon className="relative z-10 w-10 h-10 text-primary-400 mb-6" data-id="element-547" />

            <div className="relative z-10 h-[340px] sm:h-[300px] lg:h-[250px]" data-id="element-548">
              <AnimatePresence mode="wait" custom={direction} data-id="element-549">
                <motion.div key={current} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
                duration: 0.35
              }} className="absolute inset-0 flex flex-col" data-id="element-550">
                  <blockquote className="text-base sm:text-lg lg:text-xl text-navy-700 leading-relaxed font-medium flex-1 overflow-hidden" data-id="element-551">
                    «{testimonial.quote}»
                  </blockquote>
                  <div className="mt-6 flex items-center gap-4 flex-shrink-0" data-id="element-552">
                    <div className={`w-12 h-12 rounded-full ${author.companyColor} flex items-center justify-center text-white font-bold text-sm shadow-lg`} data-id="element-553">
                      {author.companyInitial}
                    </div>
                    <div data-id="element-554">
                      <div className="font-bold text-navy-900" data-id="element-555">
                        {author.author}
                      </div>
                      <div className="text-sm text-navy-500" data-id="element-556">
                        {testimonial.role}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          <div className="flex items-center justify-between mt-6 gap-3 px-2" data-id="element-557">
            <div className="flex gap-1.5 sm:gap-2 flex-wrap" data-id="element-558">
              {t.testimonials.items.map((_, i) => <button key={i} onClick={() => {
              setDirection(i > current ? 1 : -1);
              setCurrent(i);
            }} className={`h-2 sm:h-2.5 rounded-full transition-all duration-300 ${i === current ? 'bg-primary-500 w-6 sm:w-8 shadow-sm shadow-primary-500/30' : 'bg-navy-300/60 w-2 sm:w-2.5 hover:bg-navy-400'}`} aria-label={`Testimonial ${i + 1}`} data-id="element-559" />)}
            </div>
            <div className="flex gap-2 flex-shrink-0" data-id="element-560">
              <button onClick={prev} className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white border border-navy-200 flex items-center justify-center text-navy-500 hover:bg-navy-50 hover:border-navy-300 transition-all shadow-sm" data-id="element-561">
                <ChevronLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-562" />
              </button>
              <button onClick={next} className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white border border-navy-200 flex items-center justify-center text-navy-500 hover:bg-navy-50 hover:border-navy-300 transition-all shadow-sm" data-id="element-563">
                <ChevronRightIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-564" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}